import { commify, parseEther } from "@ethersproject/units";
// Provider
import { getProvider } from "utils/clients";

// Services
import { getAllowance, getCurrentEpoch, getTokenBalance } from "services/matic";
import {
  getAllowanceMaticX,
  getFromConversion,
  getInstantPoolMatic,
  getInstantPoolMaticX,
  getStakingLimitDetails,
  getToConversion,
  getTVL,
  getWithdrawalRequests,
  getXTokenBalance,
} from "services/maticx";

// Utils
import {
  getWithdrawalsWithClaimStatus,
  formatEtherNumber,
  getClaimRequestsForPolygon,
} from "utils/common";
import { NetworkConfigType } from "config/network/types";
import { maticNetworkConfig } from "constants/network";

export const getCrossChainTVL = async () => {
  try {
    const response = await fetch("https://universe.staderlabs.com/common/tvl");
    const json = await response.json();

    return commify(json.totalUSD.toFixed(0));
    // json.polygon.exchangeRate = exchangeRateJson.value;
  } catch (error) {
    return "";
  }
};

export const getTransactionConfirmation = async (
  isPolygonChainActive: boolean,
  txnHash: string
) => {
  const txReceipt = await getProvider(
    isPolygonChainActive
  ).getTransactionReceipt(txnHash);

  return txReceipt;
};

export const getWalletData = async (
  config: NetworkConfigType,
  signer: any,
  currentEpoch: number
) => {
  const isPolygonChainActive = config.chainId === "0x89";

  if (isPolygonChainActive) {
    const [tokenBalance, xTokenBalance, maticXAllowance, withdrawals] =
      await Promise.all([
        getTokenBalance(config, signer),
        getXTokenBalance(config, signer),
        getAllowanceMaticX(signer),
        getWithdrawalRequests(config, signer),
      ]);

    return {
      tokenBalance: formatEtherNumber(tokenBalance),
      xTokenBalance: formatEtherNumber(xTokenBalance),
      maticXAllowance: maticXAllowance.toString(),
      withdrawals: getClaimRequestsForPolygon(withdrawals),
    };
  } else {
    const [tokenBalance, xTokenBalance, allowanceResp, withdrawalResp] =
      await Promise.all([
        getTokenBalance(config, signer),
        getXTokenBalance(config, signer),
        getAllowance(config, signer),
        getWithdrawalRequests(config, signer),
      ]);

    return {
      tokenBalance: formatEtherNumber(tokenBalance),
      xTokenBalance: formatEtherNumber(xTokenBalance),
      maxAllowance: allowanceResp.toString(),
      withdrawals: getWithdrawalsWithClaimStatus(withdrawalResp, currentEpoch),
    };
  }
};

export const getAppData = async (config: NetworkConfigType) => {
  const isPolygonChainActive = config.chainId === "0x89";

  const [
    tvl,
    conversionToResp,
    conversionFromResp,
    crossChainTVL,
    instantPoolResp,
    instantPoolMaticResp,
  ] = await Promise.all([
    getTVL(),
    getToConversion(config, parseEther("1")),
    getFromConversion(config, parseEther("1")),
    getCrossChainTVL(),
    getInstantPoolMaticX(config),
    getInstantPoolMatic(config),
  ]);

  const instantPool = +formatEtherNumber(instantPoolResp, 0);
  const instantPoolMatic = +formatEtherNumber(instantPoolMaticResp, 0);
  const conversionRate = +formatEtherNumber(conversionToResp[0].toString(), 10);

  if (!isPolygonChainActive) {
    const currentEpoch = await getCurrentEpoch(config);

    return {
      tvl,
      conversionRate,
      conversionFromRate: +formatEtherNumber(
        conversionFromResp[0].toString(),
        10
      ),
      currentEpoch,
      crossChainTVL,
      instantPool,
      instantPoolMatic,
    };
  }

  const stakingLimitDetails = await getStakingLimitDetails(config);

  return {
    tvl,
    conversionRate,
    conversionFromRate: +formatEtherNumber(
      conversionFromResp[0].toString(),
      10
    ),
    crossChainTVL,
    instantPool,
    instantPoolMatic,
    stakingLimitDetails,
  };
};

export const switchToMainnet = async (
  key: string,
  provider: any,
  chainId: string,
  cb: () => void
) => {
  const { ethereum, okexchain, coin98, safepalProvider, trustwallet } =
    window as any;

  let executor = provider;

  if (!executor) {
    switch (key) {
      case "okex":
        executor = okexchain;
        break;

      case "coin98":
        executor = coin98.provider;
        break;

      case "safepal":
        executor = safepalProvider;
        break;

      case "trustwallet":
        executor = trustwallet;
        break;

      default:
        executor = ethereum;
    }
  }

  try {
    await executor.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId }],
    });
    cb();
  } catch (switchError) {
    try {
      await executor.request({
        method: "wallet_addEthereumChain",
        params: [maticNetworkConfig],
      });
      cb();
    } catch (addError) {
      return {
        error: addError,
      };
    }
  }
};

export const subscribeEmail = async (data: FormData) => {
  try {
    const response = await fetch(
      "https://script.google.com/macros/s/AKfycbxdo569bZMkTsQHuE3qFzO2dEwpoyOly6MbAlVUU2YlM-kMYUxcUMGYxL1_cksL1UsiPQ/exec",
      {
        method: "POST",
        body: data,
        redirect: "follow",
      }
    );

    return response;
  } catch (error) {
    return {
      error,
    };
  }
};

export const getPDFStrategies = async (data: any) => {
  try {
    const params = new URLSearchParams(data).toString();
    const url =
      "https://script.google.com/macros/s/AKfycbwzY9YCTMWy3ozs6i1IXC59ijVUQmWoynj90UOKAEuUkC97ThG8CuYeWovEWjamJcdJAA/exec";
    const response = await fetch(`${url}?${params}`, {
      redirect: "follow",
    });
    const resp = await response.json();
    return resp;
  } catch (error) {
    return {
      error,
    };
  }
};
